/**
 * <xp-browse-modes> — switches a collection banner between browse modes
 * (e.g. Shop by Category / Shop by Occasion / Shop by Budget).
 *
 * Markup contract (snippets/xp-browse-modes.liquid):
 *   [data-mode-link="<handle>"]   mode tab links; their href ends in #<handle>
 *   [data-mode-panel="<handle>"]  pill row for that mode; data-mode-title is its title
 * Banner hooks inside the nearest [data-browse-root]:
 *   [data-browse-title]           wrapper of the banner heading whose text follows the mode
 *   [data-browse-crumb]           breadcrumb items shown only while a mode is selected
 *   [data-mode-banner="<handle>"] banner photo for that mode; data-mode-banner="" is the default photo
 *
 * Only active with data-live (the top-level collection page): shows the panel named by
 * the URL hash, else the first one, and keeps the collection title when no mode is named.
 * Hash changes (menu headings or tab clicks on the same page) switch without a reload.
 */
class XpBrowseModes extends HTMLElement {
  #abort = null;

  connectedCallback() {
    // Keep the selected tab visible when the tab row scrolls (e.g. Home & Living's many categories).
    const current = this.querySelector('[data-scroller-track] [aria-current]');
    if (current) {
      const track = current.closest('[data-scroller-track]');
      const offset = current.offsetLeft - track.offsetLeft - 24;
      if (offset + current.offsetWidth > track.clientWidth) track.scrollLeft = offset;
    }
    if (!this.hasAttribute('data-live')) return;
    const panels = [...this.querySelectorAll('[data-mode-panel]')];
    if (panels.length === 0) return;
    const links = [...this.querySelectorAll('[data-mode-link]')];
    const root = this.closest('[data-browse-root]');
    const heading = root?.querySelector('[data-browse-title] h1, [data-browse-title] h2');
    const crumbs = root ? [...root.querySelectorAll('[data-browse-crumb]')] : [];
    const banners = root ? [...root.querySelectorAll('[data-mode-banner]')] : [];
    const originalTitle = heading ? heading.textContent.trim() : '';

    this.#abort = new AbortController();

    const apply = () => {
      let wanted = '';
      try {
        wanted = decodeURIComponent(window.location.hash.slice(1));
      } catch {
        wanted = '';
      }
      const match = panels.find((panel) => panel.dataset.modePanel === wanted);
      const active = match ?? panels[0];

      for (const panel of panels) panel.hidden = panel !== active;
      for (const link of links) {
        if (link.dataset.modeLink === active.dataset.modePanel) link.setAttribute('aria-current', 'true');
        else link.removeAttribute('aria-current');
      }
      const modeTitle = match ? active.dataset.modeTitle : undefined;
      if (heading) heading.textContent = modeTitle ?? originalTitle;
      for (const crumb of crumbs) crumb.hidden = !modeTitle;
      const bannerKey = match && banners.some((b) => b.dataset.modeBanner === active.dataset.modePanel) ? active.dataset.modePanel : '';
      for (const banner of banners) banner.hidden = banner.dataset.modeBanner !== bannerKey;
    };

    apply();
    window.addEventListener('hashchange', apply, { signal: this.#abort.signal });
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = null;
  }
}

if (!customElements.get('xp-browse-modes')) customElements.define('xp-browse-modes', XpBrowseModes);
